import { useEffect, useRef } from 'react';

interface UseSlideTimeoutOptions {
  isActive: boolean;
  isContentReady: boolean;
  contentDuration?: number;
  defaultDuration: number;
  onComplete: () => void;
  componentName?: string;
  log: (message: string, data?: any) => void;
}

/**
 * Custom hook that manages slide timing with failover logic.
 *
 * This hook implements a dual-timeout mechanism:
 * 1. Failover timeout: Starts immediately when slide becomes active
 *    - If content doesn't load within defaultDuration, skips to next slide
 * 2. Content timer: Starts only after content is ready
 *    - Displays content for the specified duration
 *    - Clears failover timeout when content loads
 *
 * @param options - Configuration options for the timeout behavior
 */
export const useSlideTimeout = ({
  isActive,
  isContentReady,
  contentDuration,
  defaultDuration,
  onComplete,
  componentName = 'Slide',
  log,
}: UseSlideTimeoutOptions) => {
  const failoverTimeoutRef = useRef<number | null>(null);
  const contentTimeoutRef = useRef<number | null>(null);

  // Failover timeout: if content doesn't load within defaultDuration, skip to next slide
  useEffect(() => {
    if (!isActive) {
      log('⏸️  Not active, skipping failover timeout');
      return;
    }

    log('⏲️  Setting failover timeout', { defaultDuration });

    // Start failover timeout immediately when slide becomes active
    failoverTimeoutRef.current = window.setTimeout(() => {
      if (!isContentReady) {
        log('⏰ Failover timeout fired - content not ready, skipping', {
          isContentReady,
          defaultDuration
        });
        onComplete();
      } else {
        log('✓ Failover timeout fired but content already ready, ignoring');
      }
    }, defaultDuration * 1000);

    return () => {
      if (failoverTimeoutRef.current) {
        log('🧹 Clearing failover timeout');
        clearTimeout(failoverTimeoutRef.current);
        failoverTimeoutRef.current = null;
      }
    };
  }, [isActive, defaultDuration, onComplete, isContentReady, componentName, log]);

  // Content timer: starts after content is ready
  useEffect(() => {
    log('⚙️  Content timer effect triggered', { isActive, isContentReady });

    if (!isActive) {
      log('⏸️  Not active, skipping content timer');
      return;
    }

    if (!isContentReady) {
      log('⏳ Content not ready yet, waiting');
      return;
    }

    log('✅ Content ready, starting timer');

    // Clear failover timeout since content loaded successfully
    if (failoverTimeoutRef.current) {
      log('✓ Clearing failover timeout (content ready)');
      clearTimeout(failoverTimeoutRef.current);
      failoverTimeoutRef.current = null;
    }

    const duration = contentDuration ?? defaultDuration;
    log('⏱️  Scheduling completion', { duration });

    contentTimeoutRef.current = window.setTimeout(() => {
      log('⏰ Content timer fired - completing slide');
      onComplete();
    }, duration * 1000);

    return () => {
      if (contentTimeoutRef.current) {
        log('🧹 Clearing content timer');
        clearTimeout(contentTimeoutRef.current);
        contentTimeoutRef.current = null;
      }
    };
  }, [isActive, isContentReady, contentDuration, defaultDuration, onComplete, log]);

  // Cleanup function to clear all timeouts
  const clearAllTimeouts = () => {
    log('🧹 Clearing all timeouts');
    if (failoverTimeoutRef.current) {
      clearTimeout(failoverTimeoutRef.current);
      failoverTimeoutRef.current = null;
    }
    if (contentTimeoutRef.current) {
      clearTimeout(contentTimeoutRef.current);
      contentTimeoutRef.current = null;
    }
  };

  return { clearAllTimeouts };
};
